/**
 * Integrytas Group — PFW Third-Party Portal: authenticated shell.
 *
 * After two-phase login, renders the menu of pages the signed-in portal user's
 * single company role grants (intersected live with the platform-admin ceiling —
 * pfw_portal_accessible_pages()), and the selected page's content. A view_code
 * resolves to either a saved cds dataset (rendered directly, no page chrome) or a
 * CMS page's elements (text_block + dataset). Every navigation logs a VIEW_PAGE
 * entry to the portal's own access log (pfw_portal_access_log) — separate from the
 * app's internal audit trail. Uses portal_api (the portal-token client), never
 * apiDb (which authenticates as the app's GoTrue session / anon key).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { portalRpc, portalSelect } from '../../lib/portal_api';
import type { PortalSession } from '../../lib/portal_auth';
import type { CdsRunColumn, CdsRunResult } from '../../types/cds';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { EmptyState } from '../../components/common/EmptyState';

interface AccessiblePage { view_code: string; can_add: boolean; can_change: boolean; can_delete: boolean }
interface CmsElementRow { element_id: string; element_type_code: string; sort_order: number }

function fmtCell(col: CdsRunColumn, v: unknown): string {
  if (v == null) return '—';
  if (col.format_hint === 'currency') return `$${Number(v).toFixed(2)}`;
  return String(v);
}

export function PortalHomeShell({ session, onSignOut }: { session: PortalSession; onSignOut: () => void }) {
  const { t } = useTranslation();
  const [pages, setPages] = useState<AccessiblePage[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [active, setActive] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMenu, setLoadingMenu] = useState(true);

  useEffect(() => {
    void (async () => {
      const { data, error: e } = await portalRpc<AccessiblePage[]>('pfw_portal_accessible_pages');
      if (e) { setError(e); setLoadingMenu(false); return; }
      const list = data ?? [];
      setPages(list);
      if (list.length) setActive(list[0].view_code);

      // Resolve each view_code to a display name (§17.B4 — never show a raw code
      // when a name is available): a cms_page_master_tl page_title, or a
      // cds_dataset name, whichever the code matches.
      const codes = list.map(p => p.view_code);
      const [{ data: pageRows }, { data: dsRows }] = await Promise.all([
        portalSelect<{ page_id: string; internal_code: string }[]>('cms_page_master', 'page_id, internal_code', [{ col: 'internal_code', op: 'in', value: codes }]),
        portalSelect<{ internal_code: string; name: string }[]>('cds_dataset', 'internal_code, name', [{ col: 'internal_code', op: 'in', value: codes }]),
      ]);
      const map: Record<string, string> = {};
      (dsRows ?? []).forEach(d => { map[d.internal_code] = d.name; });
      const pageIds = (pageRows ?? []).map(p => p.page_id);
      if (pageIds.length) {
        const { data: tlRows } = await portalSelect<{ page_id: string; page_title: string }[]>(
          'cms_page_master_tl', 'page_id, page_title',
          [{ col: 'page_id', op: 'in', value: pageIds }, { col: 'language_id', op: 'eq', value: 'EN_US' }],
        );
        const titleByPageId = new Map((tlRows ?? []).map(t => [t.page_id, t.page_title]));
        (pageRows ?? []).forEach(p => { const title = titleByPageId.get(p.page_id); if (title) map[p.internal_code] = title; });
      }
      setLabels(map);
      setLoadingMenu(false);
    })();
  }, []);

  return (
    <div className="flex min-h-screen bg-csm-surface-alt">
      <aside className="w-56 shrink-0 border-r border-csm-border bg-white p-3">
        <div className="mb-1 text-csm-sm font-semibold text-csm-ink">{t('portal.title', 'Partner Portal')}</div>
        <div className="mb-4 text-csm-xs text-csm-ink-soft">{session.display_name ?? t('portal.signed_in_generic', 'Signed in')}</div>
        <nav className="space-y-1">
          {loadingMenu && <div className="text-csm-xs text-csm-ink-soft">{t('common.loading', 'Loading…')}</div>}
          {!loadingMenu && pages.length === 0 && <div className="text-csm-xs text-csm-ink-soft">{t('portal.no_pages', 'No pages have been assigned to your role yet.')}</div>}
          {pages.map(p => (
            <button key={p.view_code} onClick={() => setActive(p.view_code)}
              className={`block w-full rounded px-2 py-1.5 text-left text-csm-sm ${active === p.view_code ? 'bg-primary text-white' : 'text-csm-ink hover:bg-csm-surface-alt'}`}>
              {labels[p.view_code] ?? p.view_code /* fk-display-ok: fallback when a name hasn't resolved yet / no name exists for this code */}
            </button>
          ))}
        </nav>
        <button onClick={onSignOut} className="mt-6 h-8 w-full rounded border border-csm-border bg-white text-csm-xs hover:bg-csm-surface-alt">{t('portal.sign_out', 'Sign out')}</button>
      </aside>
      <main className="min-w-0 flex-1 p-6">
        {error && (
          <ErrorBanner
            message={t('portal.load_failed', "Couldn't load your portal pages.")}
            detail={error}
            details_label={t('common.details', 'Details')}
          />
        )}
        {active && <PortalPageView key={active} viewCode={active} />}
      </main>
    </div>
  );
}

/** Resolves a view_code to a dataset (rendered directly) or a CMS page's elements. */
function PortalPageView({ viewCode }: { viewCode: string }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'loading' | 'dataset' | 'cms_page' | 'not_found'>('loading');
  const [elements, setElements] = useState<CmsElementRow[]>([]);
  const [pageId, setPageId] = useState<string | null>(null);

  useEffect(() => {
    void portalRpc('pfw_log_portal_access', { p_action: 'VIEW_PAGE', p_view_code: viewCode });
    void (async () => {
      const { data: ds } = await portalSelect<{ internal_code: string }[]>('cds_dataset', 'internal_code', [{ col: 'internal_code', op: 'eq', value: viewCode }]);
      if (ds && ds.length) { setMode('dataset'); return; }
      const { data: pg } = await portalSelect<{ page_id: string }[]>('cms_page_master', 'page_id', [{ col: 'internal_code', op: 'eq', value: viewCode }]);
      if (pg && pg.length) {
        setPageId(pg[0].page_id);
        const { data: els } = await portalSelect<CmsElementRow[]>('cms_content_element', 'element_id, element_type_code, sort_order', [{ col: 'page_id', op: 'eq', value: pg[0].page_id }]);
        setElements((els ?? []).slice().sort((a, b) => a.sort_order - b.sort_order));
        setMode('cms_page');
        return;
      }
      setMode('not_found');
    })();
  }, [viewCode]);

  if (mode === 'loading') return <div className="text-csm-sm text-csm-ink-soft">{t('common.loading', 'Loading…')}</div>;
  if (mode === 'not_found') return <div className="text-csm-sm text-csm-danger">{t('portal.page_not_found', 'This page is not available.')}</div>;
  if (mode === 'dataset') return <PortalDataset datasetCode={viewCode} />;
  return (
    <div className="space-y-4">
      {elements.map(el => el.element_type_code === 'text_block'
        ? <PortalTextBlock key={el.element_id} elementId={el.element_id} />
        : el.element_type_code === 'dataset'
          ? <PortalDatasetElement key={el.element_id} elementId={el.element_id} />
          : null)}
      {elements.length === 0 && pageId && <div className="text-csm-sm text-csm-ink-soft">{t('portal.no_content', 'No content on this page yet.')}</div>}
    </div>
  );
}

function PortalTextBlock({ elementId }: { elementId: string }) {
  const [html, setHtml] = useState('');
  useEffect(() => {
    void portalSelect<{ body_html: string }[]>('cms_text_content_tl', 'body_html', [
      { col: 'element_id', op: 'eq', value: elementId }, { col: 'language_id', op: 'eq', value: 'EN_US' },
    ]).then(({ data }) => setHtml(data?.[0]?.body_html ?? ''));
  }, [elementId]);
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

function PortalDatasetElement({ elementId }: { elementId: string }) {
  const [code, setCode] = useState<string | null>(null);
  useEffect(() => {
    void portalSelect<{ dataset_code: string }[]>('cms_dataset_content', 'dataset_code', [{ col: 'element_id', op: 'eq', value: elementId }])
      .then(({ data }) => setCode(data?.[0]?.dataset_code ?? null));
  }, [elementId]);
  if (!code) return null;
  return <PortalDataset datasetCode={code} />;
}

function PortalDataset({ datasetCode }: { datasetCode: string }) {
  const { t } = useTranslation();
  const [cols, setCols] = useState<CdsRunColumn[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setError(null);
    void portalRpc<CdsRunResult>('cds_run', { p_dataset_code: datasetCode, p_params: {} }).then(({ data, error: e }) => {
      if (e) setError(e);
      else if (!data?.ok) setError(data?.error_code ?? 'CDS.ERROR');
      else { setCols(data.columns ?? []); setRows(data.rows ?? []); }
      setLoading(false);
    });
  }, [datasetCode]);

  if (loading) return <SkeletonRows rows={8} />;
  if (error) return (
    <ErrorBanner
      message={t('cds.render.error', 'This dataset could not be loaded.')}
      detail={error}
      details_label={t('common.details', 'Details')}
    />
  );
  if (rows.length === 0) return <EmptyState variant="no-data" title={t('cds.render.empty', 'No data to display.')} />;

  return (
    <div className="overflow-x-auto rounded border border-csm-border bg-white">
      <table className="min-w-full text-csm-sm">
        <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
          <tr>{cols.map(c => <th key={c.key} className={`px-3 py-1.5 ${c.format_hint === 'currency' ? 'csm-num' : 'text-left'}`}>{c.key}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-csm-border-soft">
              {cols.map(c => <td key={c.key} className={`px-3 py-1.5${c.format_hint === 'currency' ? ' csm-num' : ''}`}>{fmtCell(c, r[c.key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
